/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { DataCreateCustomerV2, DataCreateCustomer } from '@/auth/types';
import { config } from '@/auth/api';
import { merge } from '@adobe-commerce/elsie/lib';
import { CustomerModel } from '../models';

type ApiResponse<T extends boolean> = T extends true
  ? DataCreateCustomerV2
  : DataCreateCustomer;

export const transformCreateCustomer = <T extends boolean>(
  response: ApiResponse<T>,
  apiVersion2: T
): CustomerModel => {
  let model: CustomerModel;

  if (apiVersion2) {
    const { data } = response as DataCreateCustomerV2;

    model = {
      firstName: data?.createCustomerV2?.customer?.firstname ?? '',
      lastName: data?.createCustomerV2?.customer?.lastname ?? '',
      email: data?.createCustomerV2?.customer?.email ?? '',
      customAttributes: data?.createCustomerV2?.custom_attributes ?? [],
      errors: response?.errors ?? [],
    };
  } else {
    const { data } = response as DataCreateCustomer;

    model = {
      firstName: data?.createCustomer?.customer?.firstname ?? '',
      lastName: data?.createCustomer?.customer?.lastname ?? '',
      email: data?.createCustomer?.customer?.email ?? '',
      errors: response?.errors ?? [],
    };
  }

  return merge(
    model, // default transformer
    config.getConfig().models?.CustomerModel?.transformer?.(response) // custom transformer
  );
};
